import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { ROUTES } from '@futrli/shared-services';

import { useStickyHeader } from './components/sticky-headers';
import { usePredictionsInfoContext } from '../context/predictions-info-context';
import { PredictionsSubCategories, VIEW_TYPES } from '../../../core/types';
import { getInitialScrollPosition, SCROLL_INITIAL_VALUE, useDataGridScrollContext } from './data-grid-scroll-context';
import { DIRECTION } from '../components/period-menu/period-table-control';
import { CELL_WIDTH } from '../../../core/constants';

// eslint-disable-next-line no-magic-numbers
const SCROLL_TABLE_CONTROLS_THRESHOLD = 6 * CELL_WIDTH;

const createRefs = (): React.RefObject<HTMLDivElement>[] => [
  React.createRef(),
  React.createRef(),
  React.createRef()
];

export const TABLE_REF_INDICES = {
  PREDICTED_CASH: 0,
  HEADLINES: 1,
  CONTENT: 2
};

// Data grid scrolling logic for total pages -> Profit & Loss, Balance sheet, Cash flow statement
export const useTotalPagesDataGrid = () => {
  const [tables] = useState<React.RefObject<HTMLDivElement>[]>(createRefs);
  const [headlineHeaderRef, setHeadlineHeaderScroll] = useStickyHeader();
  const { pathname } = useLocation();
  const isBalanceSheet = pathname.indexOf(ROUTES.BALANCE_SHEET) !== -1;
  const isCashFlow = pathname.indexOf(ROUTES.CASH_FLOW_STATEMENT) !== -1;

  const { periodType, totalPastDates, viewType } = usePredictionsInfoContext();

  const isPaymentsView = viewType === VIEW_TYPES.CASH_FLOW;

  const { scrollLeftRef, updateScrollPosition } = useDataGridScrollContext();

  useEffect(() => {
    let scrollLeftValue = 0;
    // There isn't cashflow view of balance sheet tab so it always needs to go
    // to default scroll position if cashflow view switch is on and we are on balance sheet tab
    if (isBalanceSheet && isPaymentsView) {
      scrollLeftValue = typeof totalPastDates === 'number' ? getInitialScrollPosition(totalPastDates) : 0;
    } else if (isCashFlow) {
      // If cashflow view is on, use current scroll position as grid position,
      // otherwise use 0 as cash flow statement tab doesn't have same columns as other tabs
      scrollLeftValue = isPaymentsView ? scrollLeftRef.current : 0;
    } else if (isPaymentsView && scrollLeftRef.current === SCROLL_INITIAL_VALUE) {
      scrollLeftValue = 0;
    } else {
      scrollLeftValue =
        scrollLeftRef.current >= 0
          ? scrollLeftRef.current
          : typeof totalPastDates === 'number'
          ? getInitialScrollPosition(totalPastDates)
          : 0;
    }

    tables.map(table => {
      if (table.current) {
        table.current.scrollLeft = scrollLeftValue;
      }
    });
    setHeadlineHeaderScroll(scrollLeftValue);
    // Reset stored scroll left position when component unmounts
    return () => {
      if ((isCashFlow && !isPaymentsView) || (isBalanceSheet && isPaymentsView)) {
        updateScrollPosition(isPaymentsView);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    viewType,
    periodType,
    totalPastDates,
    isPaymentsView,
    tables,
    isCashFlow,
    isBalanceSheet,
    setHeadlineHeaderScroll,
    scrollLeftRef.current
  ]);

  const updateScrollLeft = (scrollLeft: number): void => {
    scrollLeftRef.current = scrollLeft;
    tables.map(table => {
      if (table.current) {
        table.current.scrollLeft = scrollLeft;
      }
    });
    setHeadlineHeaderScroll(scrollLeft);
  };

  const handlePeriodTableControl = (direction: DIRECTION) => () => {
    const elmt = headlineHeaderRef.current,
      time = 500;
    if (!elmt) {
      return;
    }
    const currentPos = elmt.scrollLeft ?? 0;
    const pos =
      direction === DIRECTION.back
        ? currentPos - SCROLL_TABLE_CONTROLS_THRESHOLD
        : currentPos + SCROLL_TABLE_CONTROLS_THRESHOLD;
    let start = 0;
    const step = (currentTime: number) => {
      start = !start ? currentTime : start;
      const progress = currentTime - start;
      if (currentPos < pos) {
        updateScrollLeft(((pos - currentPos) * progress) / time + currentPos);
      } else {
        updateScrollLeft(currentPos - ((currentPos - pos) * progress) / time);
      }
      if (progress < time) {
        window.requestAnimationFrame(step);
      } else {
        elmt.scrollTo({ left: pos });
      }
    };
    window.requestAnimationFrame(step);
  };

  return {
    updateScrollLeft,
    handlePeriodTableControl,
    tables,
    headlineHeaderRef
  };
};

interface Props {
  viewOptions?: { name: string; value: VIEW_TYPES }[];
  categoryOptions?: { name: string; value: PredictionsSubCategories }[];
}

// Data grid scrolling logic for Profit & Loss category pages -> Sales, Expenses, Costs etc
export const usePredictionsDataGrid = ({ viewOptions, categoryOptions }: Props) => {
  const [tables] = useState<React.RefObject<HTMLDivElement>[]>(createRefs);
  const [headlineHeaderRef, setHeadlineHeaderScroll] = useStickyHeader();

  const { scrollLeftRef } = useDataGridScrollContext();

  const { periodType, totalPastDates, viewType, setViewType, setCategory } = usePredictionsInfoContext();

  useEffect(() => {
    if (viewType === VIEW_TYPES.CASH_FLOW) {
      updateScrollLeft(scrollLeftRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewType]);

  useEffect(() => {
    if (viewOptions?.length) {
      if (!viewType) {
        setViewType && setViewType(viewOptions[0].value);
      } else {
        setViewType && setViewType(viewType);
      }
    }
    if (categoryOptions?.length) {
      setCategory && setCategory(categoryOptions[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewType]);

  const isPaymentsView = viewType === VIEW_TYPES.CASH_FLOW;

  useEffect(() => {
    if (viewType === VIEW_TYPES.CASH_FLOW) {
      return;
    }

    const scrollLeftValue =
      scrollLeftRef.current >= 0
        ? scrollLeftRef.current
        : typeof totalPastDates === 'number'
        ? getInitialScrollPosition(totalPastDates)
        : 0;

    tables.map(table => {
      if (table.current) {
        table.current.scrollLeft = scrollLeftValue;
      }
    });
    setHeadlineHeaderScroll(scrollLeftValue);
  }, [viewType, periodType, totalPastDates, isPaymentsView, tables, setHeadlineHeaderScroll, scrollLeftRef]);

  const updateScrollLeft = (scrollLeft: number): void => {
    scrollLeftRef.current = scrollLeft;
    tables.map(table => {
      if (table.current) {
        table.current.scrollLeft = scrollLeft;
      }
    });
    setHeadlineHeaderScroll(scrollLeft);
  };

  const handlePeriodTableControl = (direction: DIRECTION) => () => {
    const elmt = headlineHeaderRef.current,
      time = 500;
    if (!elmt) {
      return;
    }
    const currentPos = elmt.scrollLeft ?? 0;
    const pos =
      direction === DIRECTION.back
        ? currentPos - SCROLL_TABLE_CONTROLS_THRESHOLD
        : currentPos + SCROLL_TABLE_CONTROLS_THRESHOLD;
    let start = 0;
    const step = (currentTime: number) => {
      start = !start ? currentTime : start;
      const progress = currentTime - start;
      if (currentPos < pos) {
        updateScrollLeft(((pos - currentPos) * progress) / time + currentPos);
      } else {
        updateScrollLeft(currentPos - ((currentPos - pos) * progress) / time);
      }
      if (progress < time) {
        window.requestAnimationFrame(step);
      } else {
        elmt.scrollTo({ left: pos });
      }
    };
    window.requestAnimationFrame(step);
  };

  return {
    updateScrollLeft,
    handlePeriodTableControl,
    tables,
    headlineHeaderRef
  };
};
